import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { UserProfile } from '../profile/profile.service';
import pDebounce from 'p-debounce';

import Analytics, { AnalyticsInstance } from 'analytics';
import { memoize, pick } from 'lodash';
import postHog from '@metro-fs/analytics-plugin-posthog';

function sanitaryUser(user: UserProfile) {
  const sanitised = pick(user, ['username', 'first_name', 'last_name', 'email', 'groups', 'is_staff', 'permissions']);

  return {
    ...sanitised,
    profile: pick(user.profile, ['browser_skin', 'language', 'dark_theme', 'mute_alerts']),
  };
}

export interface AnalyticsPlugins {
  posthog: ReturnType<typeof postHog>['methods'];
}

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  private analytics: AnalyticsInstance;

  get plugins(): AnalyticsPlugins {
    // eslint-disable-next-line
    return this.analytics.plugins as any;
  }

  constructor() {
    const plugins = [
      postHog({
        token: environment.services.posthog.token,
        enabled: environment.services.posthog.token.length > 0,
        options: {
          debug: !environment.production,
        },
      }),
    ];

    const { testsRunning } = environment;

    this.analytics = Analytics({
      app: environment.appName,
      version: environment.version,
      debug: !environment.production,
      plugins: [...(testsRunning ? [] : plugins)],
    });
  }

  async setUser(profile: UserProfile): Promise<void> {
    this.identify(profile.username, profile);
  }

  /**
   * Identify the current user
   *
   * Will only be called once per userId
   */
  private identify = memoize(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (userId: string, userProfile: UserProfile) => {
      this.analytics.identify(userId, sanitaryUser(userProfile));
    },
    (userId) => userId,
  );

  logScreenView = pDebounce(async (url: string): Promise<void> => {
    this.analytics.page({
      url,
    });
  }, 50);

  async logError(err: Error, name?: string): Promise<void> {
    if (!environment.features.analytics.errors) {
      return;
    }

    this.analytics.track('error_event', { details: err, name });
  }

  /**
   * Track an analytics event
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  track = (eventName: string, payload?: any, options?: any, callback?: (...args: any[]) => void) =>
    this.analytics.track(eventName, payload, options, callback);
}
